import { Editor, type InitOptions } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import { Editor as TinyMCEEditor } from 'tinymce';
import React, { useEffect } from 'react';

import { richTextEditorInnerStyle } from './RichTextEditorInner.css';
import { tinymceImageUploadHandler } from '../../restapi/image';
import { useDedobCss } from '../../hooks/home';

import './RichTextEditor.scss';


function generateContentStyle(dedobCss: string) {
	return richTextEditorInnerStyle +  // WFP base styles (already available in the page
		'\n\n====================\n\n' +
		dedobCss;  // dob specific styles
}

function isBold(e?: Element) {
	return e?.className?.startsWith('subheading') ||
		e?.tagName === 'STRONG' || e?.tagName === 'B';
}

const editorConfig: InitOptions = {
	min_height: 300,
	autoresize_bottom_margin: 0,
	allow_conditional_comments: false,

	// inner style config
	content_style: richTextEditorInnerStyle,  // WFP base styles (already available in the page
	body_class: 'dedob-document edit',

	// toolbar
	menubar: false,
	plugins: [
		'autoresize', 'lists', 'link', 'searchreplace', 'fullscreen', 'wordcount', 'image', 'table', 'quickbars',
	],
	toolbar: [
		'undo redo | ed1 ed2 edall | searchreplace fullscreen',
		'styles customToggleBold italic underline strikethrough superscript subscript removeformat | ' +
		'forecolor backcolor | bullist numlist alignment | link hr quickimage table',
	],
	// contextmcontextmenuenu: 'copy paste cut | link | forecolor backcolor',
	// contextmenu: 'link | forecolor backcolor',
	contextmenu: '',
	quickbars_insert_toolbar: false,
	quickbars_selection_toolbar: 'customToggleBold italic underline strikethrough removeformat | quicklink | forecolor backcolor',
	quickbars_image_toolbar: 'alignleftparent aligncenterparent alignrightparent | sizeOriginal size100 size75 size50',

	paste_as_text: false,  // we support copy/paste of formatted text and configure valid_elements and valid_styles
	valid_elements: '#p[class|style],div[class|style],-a[href|rel|target],-span[class|style],-strong/b[style],-em/i[style],-s/strike,-sup,-sub,-ul,-ol,#li,img[src|width|height|style],hr,-table[style|border],#caption,-colgroup,-col,-thead,-tbody,-tr[style],#td,#th',
	valid_styles: {'*': 'width,height,text-align,text-decoration,color,background-color,margin-left,margin-right,border,border-collapse,border-spacing,border-width,padding,border-color'},

	// styles
	style_formats: [
		{title: 'Body', block: 'p', attributes: {class: ''}},
		{title: 'Subheading', block: 'p', attributes: {class: 'subheading'}},
		{title: 'Subheading black', block: 'p', attributes: {class: 'subheading-black'}},
		{title: 'Source', block: 'p', attributes: {class: 'source'}},
		{title: 'Footnote', block: 'p', attributes: {class: 'footnote'}},
	],

	// colors
	custom_colors: false,
	color_cols: 2,
	color_cols_foreground: 3,
	color_map: [
		'#000000', 'Black',
		'#5a6872', 'Secondary text',
		'#007dbc', 'WFP brand',
		'#c5192d', 'Red',
		'#689e18', 'Green',
		'#f7b825', 'Orange',
		'#3C5979', 'National',
		'#fcc30b', 'International',
	],
	color_map_background: [
		'rgb(255, 109, 94)', '1st Edition only',
		'rgb(255, 193, 94)', '1st + 2nd Edition',
		'#fdfd77', 'Highlight yellow',
		'#62f962', 'Highlight green',
	],

	// images
	automatic_uploads: true,
	images_reuse_filename: true,  // server will make them unique
	images_upload_handler: tinymceImageUploadHandler,
	image_description: false,

	// setup
	setup: (editor: TinyMCEEditor) => {
		editor.ui.registry.addToggleButton('customToggleBold', {
			icon: 'bold',
			onAction: (_) => editor.execCommand('mceToggleFormat', false, 'bold'),
			onSetup: (api) => {
				api.setActive(editor.formatter.match('bold') || isBold(editor.selection.getNode()));
				editor.on('NodeChange', (e) => {
					api.setActive(isBold(e.element));
				});
				const changed = editor.formatter.formatChanged('bold', (state) => api.setActive(state));
				return () => changed.unbind();
			},
		});

		// all align options grouped in a single button
		editor.ui.registry.addGroupToolbarButton('alignment', {
			icon: 'align-left',
			tooltip: 'Alignment',
			items: 'alignleft aligncenter alignright alignjustify',
		});

		// custom buttons for images
		editor.ui.registry.addButton('alignleftparent', {
			icon: 'align-left',
			tooltip: 'Left image',
			onAction: () => {
				const p = editor.selection.getNode().parentElement;
				if (p) {
					editor.dom.setStyle(p, 'text-align', 'left');
				}
			},
		});
		editor.ui.registry.addButton('aligncenterparent', {
			icon: 'align-center',
			tooltip: 'Center image',
			onAction: () => {
				const p = editor.selection.getNode().parentElement;
				if (p) {
					editor.dom.setStyle(p, 'text-align', 'center');
				}
			},
		});
		editor.ui.registry.addButton('alignrightparent', {
			icon: 'align-right',
			tooltip: 'Right image',
			onAction: () => {
				const p = editor.selection.getNode().parentElement;
				if (p) {
					editor.dom.setStyle(p, 'text-align', 'right');
				}
			},
		});
		editor.ui.registry.addButton('sizeOriginal', {
			text: 'Original',
			tooltip: 'Reset image size',
			onAction: () => {
				const img = editor.selection.getNode();
				editor.dom.setAttrib(img, 'height', 'auto');
				editor.dom.setAttrib(img, 'width', 'auto');
			},
		});
		editor.ui.registry.addButton('size100', {
			text: '100%',
			tooltip: '100% of page width',
			onAction: () => {
				const img = editor.selection.getNode();
				editor.dom.setAttrib(img, 'height', 'auto');
				editor.dom.setAttrib(img, 'width', '100%');
			},
		});
		editor.ui.registry.addButton('size75', {
			text: '75%',
			tooltip: '75% of page width',
			onAction: () => {
				const img = editor.selection.getNode();
				editor.dom.setAttrib(img, 'height', 'auto');
				editor.dom.setAttrib(img, 'width', '75%');
			},
		});
		editor.ui.registry.addButton('size50', {
			text: '50%',
			tooltip: '50% of page width',
			onAction: () => {
				const img = editor.selection.getNode();
				editor.dom.setAttrib(img, 'height', 'auto');
				editor.dom.setAttrib(img, 'width', '50%');
			},
		});

		// custom buttons for restricted text
		editor.ui.registry.addButton('ed1', {
			text: '1st Edition only',
			tooltip: 'Mark text as reserved for 1st edition only',
			onAction: () => {
				editor.execCommand('RemoveFormat', false, 'BackColor');
				editor.execCommand('BackColor', false, 'rgb(255, 109, 94)');
			},
		});
		editor.ui.registry.addButton('ed2', {
			text: '1st + 2dn Edition',
			tooltip: 'Mark text as reserved for both 1st and 2nd edition',
			onAction: () => {
				editor.execCommand('RemoveFormat', false, 'BackColor');
				editor.execCommand('BackColor', false, 'rgb(255, 193, 94)');
			},
		});
		editor.ui.registry.addButton('edall', {
			text: 'All DOB',
			tooltip: 'Mark text as not reserved',
			onAction: () => editor.execCommand('RemoveFormat', false, 'BackColor'),
		});

		// quick fix for autoresize not always working at startup
		setTimeout(() => {
			try {
				editor.execCommand('mceAutoResize');
			} catch (e) {
				if (e instanceof TypeError) {
					console.warn('TinyMCE still not ready, mceAutoResize ignored');
				} else {
					throw e; // let others bubble up
				}
			}
		}, 500);
	},
};

interface Props {
	disabled?: boolean;
	initialValue: string;
	onChange: (text: string) => void;
	onInit?: (editor: TinyMCEEditor) => void;
}

export function RichTextEditor({initialValue, onChange, onInit, disabled}: Props) {
	const cssData = useDedobCss();

	useEffect(() => {
		editorConfig.content_style = generateContentStyle(cssData?.css || '');
	}, [cssData]);

	if (!cssData) return null;

	return <Editor
		licenseKey="gpl"
		tinymceScriptSrc="/tinymce/tinymce.min.js"
		disabled={disabled}
		initialValue={initialValue}
		init={editorConfig}
		onInit={(_, editor) => onInit && onInit(editor)}
		onEditorChange={onChange}
	/>;
}
